import mapboxgl from "mapbox-gl";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	defaultUserPreferences,
	resolveDefaultUserPreferences,
	UserPreferences,
	VelavuOrganization,
	VelavuPermission,
	VelavuUser,
} from "velavu-js-api";
import { useIsSignedInToAmplify } from "./use-is-signed-in-to-amplify";
import useOrganizePermissions from "./use-organize-permissions";
import { useUserOrganization } from "./use-user-organization";

export type HasPermissionFunction = (
	permission: VelavuPermission,
) => boolean | undefined;

interface SignInContextType {
	//Whether we are fetching user information
	isLoading: boolean;
	//Whether the user is signed in
	isSignedIn: boolean;

	//The current logged in velavu user, or undefined if not signed in or loading
	velavuUser?: VelavuUser;
	updateVelavuUser: (user: Partial<VelavuUser>) => Promise<VelavuUser>;
	updateVelavuUserImage: (imageData: string | undefined) => void;

	//Preferences resolved against defaults
	preferences: UserPreferences;

	//The user's organization
	organization?: VelavuOrganization;
	updateOrganization: (
		organization: Partial<VelavuOrganization>,
	) => Promise<VelavuOrganization>;

	//Returns whether the user has a specific permission, or undefined if the user is not loaded yet
	hasPermission: HasPermissionFunction;

	//Load flags
	isMapboxRegistered: boolean;
}

const SignInContext = React.createContext<SignInContextType>({
	isLoading: true,
	isSignedIn: false,
	updateVelavuUser() {
		throw new Error("Velavu user handler not assigned");
	},
	updateVelavuUserImage() {
		throw new Error("Velavu user image handler not assigned");
	},
	updateOrganization() {
		throw new Error("Organization handler not assigned");
	},
	hasPermission() {
		throw new Error("Permission handler not assigned");
	},
	preferences: defaultUserPreferences,
	isMapboxRegistered: false,
});

export interface SignInContextProviderProps {
	children: React.ReactNode;
}

export default function SignInContextProvider(
	props: SignInContextProviderProps,
) {
	const { isLoading, isSignedIn } = useIsSignedInToAmplify();

	const {
		user: velavuUser,
		updateVelavuUser,
		updateVelavuUserImage,
		organization,
		updateOrganization,
	} = useUserOrganization(isSignedIn);

	//Update the mapbox token with the user's token
	const [isMapboxRegistered, setIsMapboxRegistered] = useState(false);
	useEffect(() => {
		if (velavuUser === undefined) {
			setIsMapboxRegistered(false);
		} else {
			mapboxgl.accessToken = velavuUser.mapbox_token;
			setIsMapboxRegistered(true);
		}
	}, [velavuUser, setIsMapboxRegistered]);

	//Resolve the user settings against defaults
	const resolvedPreferences = useMemo(() => {
		return resolveDefaultUserPreferences(velavuUser?.settings);
	}, [velavuUser?.settings]);

	//Apply the user's language preference to i18next
	const { i18n } = useTranslation();
	useEffect(() => {
		i18n.changeLanguage(resolvedPreferences.language ?? undefined);
	}, [resolvedPreferences.language, i18n]);

	const { hasPermission } = useOrganizePermissions(velavuUser?.permissions);

	const contextValue: SignInContextType = useMemo(() => {
		return {
			isLoading,
			isSignedIn,
			velavuUser,
			updateVelavuUser,
			updateVelavuUserImage,
			preferences: resolvedPreferences,
			organization,
			updateOrganization,
			hasPermission,
			isMapboxRegistered,
		};
	}, [
		isLoading,
		isSignedIn,
		velavuUser,
		updateVelavuUser,
		updateVelavuUserImage,
		resolvedPreferences,
		organization,
		updateOrganization,
		hasPermission,
		isMapboxRegistered,
	]);

	return (
		<SignInContext.Provider value={contextValue}>
			{props.children}
		</SignInContext.Provider>
	);
}

export function useSignInContext(): SignInContextType {
	return useContext(SignInContext);
}

/**
 * Helper function to check if the user has a specific permission
 */
export function useHasPermission(
	permission: VelavuPermission,
): boolean | undefined {
	const { hasPermission } = useSignInContext();
	return hasPermission(permission);
}
